import {
  ChangeDetectorRef,
  Component,
  Injector,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { PrFormSelectListComponent } from '@app/components/pr-form/pr-form-select-list/pr-form-select-list.component';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrModalComponent } from '@app/components/pr-modal/pr-modal/pr-modal.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { API_ROUTE_SUPPORTS_SEARCH } from '@app/forms/subscription/api/api-router';
import { InfoOption } from '@app/forms/subscription/components/info/info.component';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { infoOption } from '@app/forms/subscription/services/ref';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { combineLatest, Observable, Subject, debounceTime, map, mergeMap, takeUntil, tap } from 'rxjs';
import { SupportsTable } from '../../shared/supports-table/supports-table.model';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

@UntilDestroy()
@Component({
    selector: 'versement-libres-supports-form',
    templateUrl: './versement-libres-supports-form.component.html',
    styleUrls: ['./versement-libres-supports-form.component.scss'],
    standalone: false
})
export class VersementLibresSupportsFormComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  optionInfo1: InfoOption = infoOption;

  get supportsTable(): SupportsTable {
    const config: SupportsTable = {
      title: this.isComplementaire
        ? 'Supports du versement'
        : 'Supports du versement programmé',
      addLabel: 'Ajouter un support',
      removable: true,
      removeComment:
        'Toute suppression est définitive, les données seront perdues.',
      totalLabel: 'Total réparti',
      emptyLabel: 'Aucun support sélectionné pour le moment.',
      columns: [
        { label: 'Support', type: 'support' },
        {
          label: 'En €',
          type: 'input',
          controlName: 'amount',
          unit: '€',
          digits: '1.2-2',
          step: '0.01',
          min: 0,
          ariaLabel: 'Versement en euros',
          total: true,
        },
      ],
    };

    if (!this.hasVersementAmount) return config;

    return {
      ...config,
      targetPercent: 100,
      columns: [
        config.columns[0],
        {
          label: 'En %',
          type: 'input',
          controlName: 'percentage',
          unit: '%',
          digits: '1.0-4',
          step: '0.0001',
          min: 0,
          max: 100,
          ariaLabel: 'Versement en pourcentage',
          total: true,
        },
        {
          ...config.columns[1],
          max: () => this.versementAmount || 0,
        },
      ],
    };
  }

  @ViewChild('formSelectList', {
    static: true,
    read: PrFormSelectListComponent,
  })
  formSelectList: PrFormSelectListComponent;

  @ViewChild('modalSupport', { static: true, read: PrModalComponent })
  modalSupport: PrModalComponent;

  criteriaSupportForm: UntypedFormGroup;
  supportTypes;

  supports: any[] = [];

  currentSupportPage = 0;
  supportsPageSize = 15;
  supportsLength = 0;

  private destroy$ = new Subject<void>();

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    protected injector: Injector,
    private cdr: ChangeDetectorRef,
    private referenceDao: ReferenceDao,
    private httpApi: HttpApiService,
  ) {
    super(formConstructor);
    this.criteriaSupportForm = this.formBuilder.group({
      autocomplete: [],
      supportTypes: [],
      enableViewAll: [false],
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngOnInit() {
    this.subscriptionFacade.initForm(this);
    this.attachVersementSync(this.destroy$);
    this.formSelectList.loading = true;
    this.supportTypes = combineLatest([
      this.referenceDao.getSupportTypes(),
      this.search(),
    ]).pipe(
      map(([supportTypes, search]) => supportTypes),

      tap(data => {}),
    );

    this.criteriaSupportForm.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => (this.formSelectList.loading = true)),
        debounceTime(500),
        mergeMap(this.search.bind(this)),
      )
      .subscribe();
  }

  search(): Observable<any> {
    const searchOptions = this.buildSearchOptions();

    return this.searchSupports(searchOptions).pipe(
      takeUntil(this.destroy$),
      map(res => {
        this.formSelectList.loading = false;
        this.supportsLength = res.totalElements;
        this.supports = res.content || [];
        return res;
      }),
      tap(() => {
        this.cdr.detectChanges();
      }),
    );
  }

  changeSupportsPage(event) {
    this.currentSupportPage = event.pageIndex;
    this.formSelectList.loading = true;
    this.search().subscribe();
  }

  private searchSupports(searchOptions: any): Observable<any> {
    return this.httpApi
      .post(API_ROUTE_SUPPORTS_SEARCH, {
        ...searchOptions,
        active: true,
      })
      .pipe(takeUntil(this.destroy$));
  }

  private buildSearchOptions(): any {
    return {
      page: this.currentSupportPage + 1,
      size: this.supportsPageSize,
      autocomplete: this.criteriaSupportForm.value.autocomplete,
      supportTypes: this.criteriaSupportForm.value.supportTypes
        ? [this.criteriaSupportForm.value.supportTypes]
        : undefined,
      lifeInsuranceId: this.productId,
      noStrike: !this.criteriaSupportForm.value.enableViewAll,
      amendmentMapped: true,
      isAvailableInSimulationAndSubscription: true,
      fillFullData: true,
    };
  }

  onSubmit() {
    this.form.markAsDirty();
    // PRE-4896 : préremplit les 4 champs "*Autocomplete" de la slide "Taux par support" (versement),
    // pour tous les supports sélectionnés ici, quelle que soit l'organisation.
    this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  disabledNextBtn() {
    if (this.hasVersementAmount) {
      return !this.isFullyAllocated;
    }

    return this.hasNotInvestment();
  }

  hasNotInvestment() {
    const supportRepartition = this.supportRepartition as UntypedFormArray;
    const investment = supportRepartition.controls.map(control => {
      return Number(control.get('amount').value);
    });

    const totalRepartition = investment.reduce((acc, value) => acc + value, 0);
    return totalRepartition === 0;
  }

  addSupport() {
    this.modalSupport.open();
  }

  validateAddSupport() {
    this.modalSupport.close();
  }

  getPerf(support: any): string {
    if (support?.details?.performances?.perfP1Y)
      return (support?.details?.performances?.perfP1Y * 100).toFixed(2) + ' %';
    else return '~';
  }
  getPerfColor(support: any): string {
    let color = 'neutral';
    if (support?.details?.performances?.perfP1Y) {
      if (support?.details?.performances?.perfP1Y > 0.05) color = 'success';
      else if (support?.details?.performances?.perfP1Y < -0.05)
        color = 'danger';
      else color = 'warning';
    }
    return color;
  }
  getRisk(support: any): string {
    if (support?.details?.profilRisk?.value)
      return support?.details?.profilRisk?.value;
    else return '~';
  }
  getRiskColor(support: any): string {
    let color = 'neutral';
    if (support?.details?.profilRisk?.value) {
      if (support?.details?.profilRisk?.value >= 5) color = 'danger';
      else if (support?.details?.profilRisk?.value >= 3) color = 'warning';
      else color = 'success';
    }
    return color;
  }

  get isComplementaire(): boolean {
    return this.entity?.operation === 'VERSEMENT_COMPLEMENTAIRE';
  }

  onSupportSelect(support) {
    let newControl = this.formBuilder.group(
      this.createModel({
        supportId: support.id,
        supportName: support.name,
        isin: support.isin,
        isr: support.isr,
        supportType: support.type,
        perfP3Y: support.details?.performances?.perfP3Y,
        volat3Y: support.details?.performances?.volat3Y,
        risk: support.details?.profilRisk?.value,
        ...support,
        amount: 0,
        percentage: 0,
      }),
    );
    let formArray = this.supportRepartition as UntypedFormArray;
    newControl.setValue({ ...newControl.value, index: formArray.value.length });
    formArray.push(newControl);
    // set parent form as dirty
    this.form.markAsDirty();
  }

  onSupportDeselect(support) {
    let formArray = this.supportRepartition as UntypedFormArray;
    const control = formArray.controls.findIndex(
      c =>
        (!!support?.isin && c.value.isin === support.isin) ||
        (support?.id != null && c.value.id == support.id),
    );
    if (control < 0) return;
    formArray.removeAt(control);
    this.form.markAsDirty();
  }

  getSupportInvestment(isin) {
    const currentSupports = this.supportRepartition.value;
    return _.map(currentSupports, 'isin').includes(isin);
  }

  onMoreInfoClick(support) {
    window.open(`#/dashboard/assets/supports/${support.id}`, '_blank');
  }
}
